/**
 * Drag-and-drop list for level ordering.
 */
import * as React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { Level } from '@/types/level.types';
import { cn } from '@/lib/utils';
import { buildLevelSortableItems, type LevelSortableItem } from './level-form-utils';

/** Sentinel id for the unsaved draft row in the sortable list. */
export const NEW_LEVEL_PLACEHOLDER_ID = '__new__';

/** One draggable level row in the sortable list. */
const SortableItem: React.FC<{ item: LevelSortableItem; isCurrent: boolean }> = ({
  item,
  isCurrent,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors',
        isCurrent
          ? 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)] border-[var(--color-2-nuance-1)]'
          : 'bg-card border-border hover:bg-muted/40',
        item.isPlaceholder && 'border-dashed',
        isDragging && 'shadow-lg',
      )}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        {...attributes}
        {...listeners}
        aria-label="Déplacer"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 flex-1 min-w-0">
        {item.code && (
          <span className="font-mono text-xs text-muted-foreground shrink-0">{item.code}</span>
        )}
        <span
          className={cn(
            'text-sm truncate',
            isCurrent ? 'font-semibold text-foreground' : 'text-foreground',
            item.isPlaceholder && 'italic text-muted-foreground',
          )}
        >
          {item.label}
        </span>
        {isCurrent && !item.isPlaceholder && (
          <span className="text-xs text-[var(--color-2-nuance-1)] font-medium shrink-0">
            (en cours)
          </span>
        )}
        {item.isPlaceholder && (
          <span className="text-xs text-[var(--color-2-nuance-1)] font-medium shrink-0">
            (nouveau)
          </span>
        )}
      </div>
    </div>
  );
};

/** Props for {@link LevelSortableList}. */
interface LevelSortableListProps {
  allLevels: Level[];
  currentLevelId: string;
  newLevelLabel?: string;
  onOrderChange: (orderedIds: string[]) => void;
}

/** Drag-and-drop list for level ordering. */
export const LevelSortableList: React.FC<LevelSortableListProps> = ({
  allLevels,
  currentLevelId,
  newLevelLabel,
  onOrderChange,
}) => {
  const isCreate = currentLevelId === NEW_LEVEL_PLACEHOLDER_ID;

  const [items, setItems] = React.useState<LevelSortableItem[]>(() =>
    buildLevelSortableItems(allLevels, isCreate, newLevelLabel, NEW_LEVEL_PLACEHOLDER_ID),
  );

  React.useEffect(() => {
    setItems(buildLevelSortableItems(allLevels, isCreate, newLevelLabel, NEW_LEVEL_PLACEHOLDER_ID));
  }, [allLevels, isCreate, newLevelLabel]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((l) => l.id === active.id);
      const newIndex = prev.findIndex((l) => l.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      onOrderChange(reordered.map((l) => l.id));
      return reordered;
    });
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((l) => l.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5">
          {items.map((item) => (
            <SortableItem key={item.id} item={item} isCurrent={item.id === currentLevelId} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
